import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import {
  Search,
  Filter,
  Plus,
  Settings,
  X,
  Boxes,
  Package,
  Wrench,
  ClipboardList,
  ShoppingCart,
  PackageCheck,
  Tag,
  ArrowRightLeft,
  QrCode,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import StockTable from "../components/inventory/StockTable";
import AdjustmentTable from "../components/inventory/AdjustmentTable";
import RequestTable from "../components/inventory/RequestTable";
import ProcurementTable from "../components/inventory/ProcurementTable";
import ReceivingTable from "../components/inventory/ReceivingTable";
import PurchaseOrderTable from "../components/inventory/PurchaseOrderTable";
import ConversionRulesTable from "../components/conversion/ConversionRulesTable";
import AddProcurementModal from "../components/AddProcurementModal"; // Using as Add Request (PO)
import StockConfigModal from "../components/StockConfigModal";
import StockAdjustmentModal from "../components/StockAdjustmentModal"; // Using as Add Adjustment
import AddSellingUnitsModal from "../components/AddSellingUnitsModal";
import ConfirmationModal from "../components/ConfirmationModal";
import ProcurementDetailModal from "../components/inventory/ProcurementDetailModal";
import RequestDetailModal from "../components/inventory/RequestDetailModal";
import AdjustmentDetailModal from "../components/inventory/AdjustmentDetailModal";
import ProcurementInvoice from "../components/inventory/ProcurementInvoice";
import AddonStockTable from "../components/inventory/AddonStockTable";
import DataBarcodeLabelTable from "../components/master/DataBarcodeLabelTable";
import CreateBarcodeLabelModal, { BarcodeLabelQuickPrint } from "../components/master/CreateBarcodeLabelModal";
import SearchableSelect from "../components/SearchableSelect";
import Modal from "../components/Modal";
import ExcelIcon from "../components/ExcelIcon";
import { exportInventoryStockToExcel } from "../utils/inventoryStockExcelExport";
import * as inventoryService from "../api/inventoryService";
import conversionService from "../api/conversionService";
import barcodeLabelService from "../api/barcodeLabelService";
import { masterService, categoryService } from "../api/masterService";
import { configService } from "../api/configService";
import { useNotification } from "../contexts/NotificationContext";

export default function Inventory() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [isExportingStock, setIsExportingStock] = useState(false);
  const [storeInfo, setStoreInfo] = useState({
    name: localStorage.getItem("tenantName") || "Toko",
    address: "",
  });

  useEffect(() => {
    configService
      .getStore()
      .then((res) => {
        const s = res?.data ?? res ?? {};
        setStoreInfo({
          name: s?.name || localStorage.getItem("tenantName") || "Toko",
          address: s?.settings?.address || s?.address || "",
        });
      })
      .catch(() => {});
  }, []);

  // Print Ref and State
  const printComponentRef = useRef();
  const [printData, setPrintData] = useState(null);

  const handlePrint = useReactToPrint({
    contentRef: printComponentRef,
    onAfterPrint: () => setPrintData(null),
    documentTitle: "Purchase Order",
    pageStyle: `
      @page {
        size: A5 landscape;
        margin: 5mm;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      }
    `,
  });

  useEffect(() => {
    if (printData) {
      // Add a small delay to ensure DOM is updated with new data
      const timer = setTimeout(() => {
        handlePrint();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [printData, handlePrint]);
  const getActiveTabFromPath = () => {
    const path = location.pathname;
    if (path.includes("/persediaan")) return "Persediaan";
    if (path.includes("/penyesuaian")) return "Penyesuaian";
    if (
      path.includes("/purchase-order") ||
      path.includes("/po") ||
      path.includes("/permintaan") ||
      path.includes("/pengadaan") ||
      path.includes("/penerimaan")
    )
      return "Purchase Order";
    if (path.includes("/konversi")) return "Konversi Stok";
    if (path.includes("/stok-ekstra")) return "Stok Ekstra";
    if (path.includes("/label-barcode")) return "Label Barcode";
    return "Persediaan"; // Default
  };

  const activeTab = getActiveTabFromPath();
  const isRetail = localStorage.getItem("PREVIEW_APPS") !== "true";

  // Redirect /inventori to /inventori/persediaan (and redirect invalid tab routes per app mode)
  useEffect(() => {
    if (
      location.pathname === "/inventori" ||
      (isRetail && location.pathname.includes("/stok-ekstra")) ||
      (!isRetail && (
        location.pathname.includes("/purchase-order") ||
        location.pathname.includes("/po") ||
        location.pathname.includes("/permintaan") ||
        location.pathname.includes("/pengadaan") ||
        location.pathname.includes("/penerimaan") ||
        location.pathname.includes("/label-barcode")
      ))
    ) {
      navigate("/inventori/persediaan", { replace: true });
    }
  }, [location.pathname, navigate, isRetail]);

  // Modals
  const [isAddPOModalOpen, setIsAddPOModalOpen] = useState(false);
  const [isAdjustmentModalOpen, setIsAdjustmentModalOpen] = useState(false);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [isAddRuleModalOpen, setIsAddRuleModalOpen] = useState(false); // Deprecated
  const [isAddSellingUnitsModalOpen, setIsAddSellingUnitsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductVariants, setSelectedProductVariants] = useState([]);
  const [selectedProductRules, setSelectedProductRules] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteIds, setDeleteIds] = useState([]);
  const [loadingEditId, setLoadingEditId] = useState(null);
  const [editingUnitId, setEditingUnitId] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);

  // Barcode Label States
  const [barcodeLabelData, setBarcodeLabelData] = useState([]);
  const [isCreateBarcodeModalOpen, setIsCreateBarcodeModalOpen] = useState(false);
  const [selectedBarcodeLabel, setSelectedBarcodeLabel] = useState(null);
  const [quickPrintBarcodeLabel, setQuickPrintBarcodeLabel] = useState(null);
  const [isBarcodeDeleteModalOpen, setIsBarcodeDeleteModalOpen] = useState(false);
  const [barcodeLabelToDelete, setBarcodeLabelToDelete] = useState(null);
  const [barcodeDeleteLoading, setBarcodeDeleteLoading] = useState(false);

  // Conversion States
  const [filterProduct, setFilterProduct] = useState("");
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [conversionRules, setConversionRules] = useState([]);
  const [stockConversionRules, setStockConversionRules] = useState([]);

  // Table State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");

  // Purchase Order States
  const [poStatusFilter, setPoStatusFilter] = useState("ALL");
  const [purchaseOrderData, setPurchaseOrderData] = useState([]);

  // Data States
  const [stockData, setStockData] = useState([]);
  const [adjustmentData, setAdjustmentData] = useState([]);
  const [requestData, setRequestData] = useState([]);
  const [procurementData, setProcurementData] = useState([]);
  const [receivingData, setReceivingData] = useState([]);
  const [isProcurementDetailModalOpen, setIsProcurementDetailModalOpen] =
    useState(false);
  const [procurementDetailLoading, setProcurementDetailLoading] =
    useState(false);
  const [procurementActionLoading, setProcurementActionLoading] =
    useState(false);
  const [procurementDetail, setProcurementDetail] = useState(null);

  const [isRequestDetailModalOpen, setIsRequestDetailModalOpen] =
    useState(false);
  const [requestDetailLoading, setRequestDetailLoading] = useState(false);
  const [requestActionLoading, setRequestActionLoading] = useState(false);
  const [requestDetail, setRequestDetail] = useState(null);

  const [isAdjustmentDetailModalOpen, setIsAdjustmentDetailModalOpen] =
    useState(false);
  const [adjustmentDetailLoading, setAdjustmentDetailLoading] = useState(false);
  const [adjustmentActionLoading, setAdjustmentActionLoading] = useState(false);
  const [adjustmentDetail, setAdjustmentDetail] = useState(null);

  // Meta/Pagination States
  const [meta, setMeta] = useState({ page: 1, limit: 10, total: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Cache semua data stok (sudah dikelompokkan per produk) untuk paginasi per induk
  const [allStockGroups, setAllStockGroups] = useState([]);

  // Filter Kategori (untuk tab Persediaan)
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterMenuRef = useRef(null);

  // Load categories untuk filter
  useEffect(() => {
    let cancelled = false;
    categoryService
      .list({ limit: 100 })
      .then((res) => {
        if (!cancelled) setCategories(res?.data || []);
      })
      .catch(() => { });
    return () => {
      cancelled = true;
    };
  }, []);

  // Close filter popup saat klik di luar
  useEffect(() => {
    if (!isFilterOpen) return;
    const handleClickOutside = (e) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(e.target)) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isFilterOpen]);

  // Fetch data from API
  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        query: searchTerm,
      };

      switch (activeTab) {
        case "Persediaan": {
          // Data persediaan di-fetch semua halaman lalu dipaginasi per produk
          // (induk) oleh effect khusus, bukan per baris varian seperti API.
          break;
        }
        case "Penyesuaian": {
          const response = await inventoryService.getStockAdjustments(params);
          const rawItems = response.data || [];
          const filteredItems = rawItems.filter(
            (item) => !item.notes?.toLowerCase().includes("konfigurasi stok")
          );
          setAdjustmentData(filteredItems);
          setMeta(
            response.meta
              ? { ...response.meta, total: response.meta.total ? Math.min(response.meta.total, filteredItems.length) : filteredItems.length }
              : { page: 1, limit: 10, total: filteredItems.length }
          );
          break;
        }
        case "Purchase Order": {
          const poParams = {
            ...params,
          };
          if (poStatusFilter && poStatusFilter !== "ALL") {
            poParams.status = poStatusFilter;
          }
          const response = await inventoryService.getPurchaseOrders(poParams);
          const rawOrders = response.data || [];
          setPurchaseOrderData(rawOrders);
          setMeta(
            response.meta || {
              page: currentPage,
              limit: itemsPerPage,
              total: rawOrders.length,
            }
          );
          break;
        }
        case "Permintaan": {
          // Filter PO with status SUBMITTED, REJECTED, APPROVED
          const response = await inventoryService.getPurchaseOrders({
            ...params,
            status: "SUBMITTED,REJECTED,APPROVED",
          });
          setRequestData(response.data || []);
          setMeta(response.meta || { page: 1, limit: 10, total: 0 });
          break;
        }
        case "Pengadaan": {
          // Filter PO with status APPROVED, REJECTED, ORDERED
          const response = await inventoryService.getPurchaseOrders({
            ...params,
            status: "APPROVED,REJECTED,ORDERED,SUBMITTED",
          });
          setProcurementData(response.data || []);
          setMeta(response.meta || { page: 1, limit: 10, total: 0 });
          break;
        }
        case "Penerimaan": {
          const response = await inventoryService.getPurchaseOrders({
            ...params,
            status: "ORDERED,COMPLETED,PARTIAL_RECEIVED",
          });
          setReceivingData(response.data || []);
          setMeta(response.meta || { page: 1, limit: 10, total: 0 });
          break;
        }
        case "Konversi Stok": {
          const convParams = {
            limit: itemsPerPage,
            offset: (currentPage - 1) * itemsPerPage,
            q: searchTerm,
          };
          if (categoryFilter) {
            convParams.category_id = categoryFilter;
          }

          const response = await conversionService.rules.list(convParams);
          setConversionRules(
            response.data?.items || response.data || response || []
          );
          setMeta({
            page: currentPage,
            limit: itemsPerPage,
            total: response.data?.total || response.data?.items?.length || 0,
          });
          break;
        }
        case "Label Barcode": {
          const response = await barcodeLabelService.list({
            search: searchTerm,
            page: currentPage,
            limit: itemsPerPage,
          });
          setBarcodeLabelData(response.data || []);
          setMeta(
            response.meta || {
              page: currentPage,
              limit: itemsPerPage,
              total: (response.data || []).length,
            }
          );
          break;
        }
        default:
          break;
      }
    } catch (err) {
      console.error("Failed to fetch inventory data:", err);
      setError(err.message || "Gagal memuat data");
      showNotification("error", err.message || "Gagal memuat data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch products for Konversi filter
  useEffect(() => {
    const fetchProducts = async () => {
      if (activeTab === "Konversi Stok") {
        try {
          setLoadingProducts(true);
          const response = await masterService.products.list({
            status: "ACTIVE",
          });
          setProducts(response.data || []);
        } catch (error) {
          console.error("Error fetching products:", error);
        } finally {
          setLoadingProducts(false);
        }
      }
    };
    fetchProducts();
  }, [activeTab]);

  useEffect(() => {
    fetchData();
  }, [
    activeTab,
    currentPage,
    itemsPerPage,
    searchTerm,
    refreshTrigger,
    filterProduct,
    categoryFilter,
    poStatusFilter,
  ]);

  // Fetch SEMUA halaman data stok saat filter/pencarian berubah,
  // lalu kelompokkan per produk (induk) untuk paginasi per induk.
  useEffect(() => {
    if (activeTab !== "Persediaan") return;
    let cancelled = false;
    const loadAllStocks = async () => {
      setLoading(true);
      setError(null);
      try {
        const stockParams = { query: searchTerm };
        if (categoryFilter) stockParams.category_id = categoryFilter;

        const all = [];
        let page = 1;
        let total = Infinity;
        while (page <= 50) {
          const resp = await inventoryService.getStocks({ ...stockParams, page, limit: 100 });
          const rows = resp?.data || [];
          all.push(...rows);
          const metaTotal = Number(resp?.meta?.total ?? 0);
          if (metaTotal > 0) total = metaTotal;
          if (all.length >= total || rows.length === 0) break;
          page += 1;
        }
        if (cancelled) return;

        // Grouping sama persis dengan logika di StockTable
        const isApps = localStorage.getItem("PREVIEW_APPS") === "true";
        const filteredData = isApps
          ? all.filter((item) => !item.brand_name && !item.brand_id)
          : all;
        const map = {};
        filteredData.forEach((item) => {
          const key = item.product_id || item.id;
          if (!map[key]) map[key] = { id: key, variants: [] };
          map[key].variants.push(item);
        });
        const groups = Object.values(map);
        groups.forEach((product) => {
          if (product.variants.length > 1) {
            product.variants = product.variants.filter(
              (v) => v.variant_id != null || v.option_id != null
            );
          }
        });

        setAllStockGroups(groups);
      } catch (err) {
        if (!cancelled) setError(err.message || "Gagal memuat data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    setAllStockGroups([]);
    loadAllStocks();
    return () => {
      cancelled = true;
    };
  }, [activeTab, searchTerm, categoryFilter, refreshTrigger]);

  // Slice cache per halaman → data tabel (hanya induk yang dihitung paginasi)
  useEffect(() => {
    if (activeTab !== "Persediaan") return;
    const start = (currentPage - 1) * itemsPerPage;
    const pageGroups = allStockGroups.slice(start, start + itemsPerPage);
    setStockData(pageGroups.flatMap((group) => group.variants));
    setMeta({
      page: currentPage,
      limit: itemsPerPage,
      total: allStockGroups.length,
    });
  }, [activeTab, currentPage, itemsPerPage, allStockGroups]);

  // Aturan ini dipakai untuk menampilkan stok dalam satuan konversinya
  // tanpa mengganggu pemuatan tabel persediaan jika endpoint konversi gagal.
  useEffect(() => {
    let cancelled = false;

    if (activeTab !== "Persediaan") {
      setStockConversionRules([]);
      return () => {
        cancelled = true;
      };
    }

    const loadStockConversionRules = async () => {
      try {
        const params = categoryFilter ? { category_id: categoryFilter } : {};
        const response = await conversionService.rules.list(params);
        if (!cancelled) {
          setStockConversionRules(response.data?.items || []);
        }
      } catch (err) {
        console.error("Failed to fetch stock conversion rules:", err);
        if (!cancelled) {
          setStockConversionRules([]);
        }
      }
    };

    loadStockConversionRules();

    return () => {
      cancelled = true;
    };
  }, [activeTab, categoryFilter, refreshTrigger]);

  const tabs = [
    {
      label: "Persediaan",
      icon: Package,
      count: activeTab === "Persediaan" ? meta.total : undefined,
    },
    {
      label: "Penyesuaian",
      icon: Wrench,
      count: activeTab === "Penyesuaian" ? meta.total : undefined,
    },
    {
      label: "Konversi Stok",
      icon: ArrowRightLeft,
      count: activeTab === "Konversi Stok" ? meta.total : undefined,
    },
    ...(!isRetail
      ? [
          {
            label: "Stok Ekstra",
            icon: Tag,
            count: undefined,
          },
        ]
      : []),
    ...(isRetail
      ? [
          {
            label: "Label Barcode",
            icon: QrCode,
            count: activeTab === "Label Barcode" ? meta.total : undefined,
          },
          {
            label: "Purchase Order",
            icon: ShoppingCart,
            count: activeTab === "Purchase Order" ? meta.total : undefined,
          },
        ]
      : []),
  ];

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const handleExportStockExcel = async () => {
    if (isExportingStock) return;
    try {
      setIsExportingStock(true);

      let selectedCatName = "";
      if (categoryFilter) {
        const found = categories.find((c) => String(c.id) === String(categoryFilter));
        selectedCatName = found?.name || "";
      }

      let groupsToExport = allStockGroups;
      if (!groupsToExport || groupsToExport.length === 0) {
        const stockParams = { query: searchTerm };
        if (categoryFilter) stockParams.category_id = categoryFilter;
        const all = [];
        let page = 1;
        while (page <= 50) {
          const resp = await inventoryService.getStocks({ ...stockParams, page, limit: 100 });
          const rows = resp?.data || [];
          all.push(...rows);
          if (rows.length === 0 || all.length >= Number(resp?.meta?.total ?? 0)) break;
          page += 1;
        }
        const map = {};
        all.forEach((item) => {
          const key = item.product_id || item.id;
          if (!map[key]) map[key] = { id: key, variants: [] };
          map[key].variants.push(item);
        });
        groupsToExport = Object.values(map);
      }

      if (!groupsToExport || groupsToExport.length === 0) {
        showNotification("warning", "Tidak ada data persediaan stok untuk diexport");
        return;
      }

      await exportInventoryStockToExcel({
        storeInfo,
        stockGroups: groupsToExport,
        categoryName: selectedCatName,
        searchTerm,
      });

      showNotification("success", "Data persediaan stok berhasil diexport ke Excel");
    } catch (err) {
      console.error("Export Stock Excel failed:", err);
      showNotification("error", err?.message || "Gagal mengexport data persediaan stok ke Excel");
    } finally {
      setIsExportingStock(false);
    }
  };

  const handleInfo = (id) => {
    switch (activeTab) {
      case "Persediaan":
        navigate(`/inventori/stok/${id}`);
        break;
      case "Penyesuaian":
        handleOpenAdjustmentDetail(id);
        break;
      case "Purchase Order":
        navigate(`/inventori/purchase-orders/${id}`);
        break;
      case "Permintaan":
        handleOpenRequestDetail(id);
        break;
      case "Pengadaan":
        handleOpenProcurementDetail(id);
        break;
      case "Penerimaan":
        navigate(`/inventori/penerimaan/${id}`, {
          state: { type: "receiving" },
        });
        break;
      default:
        console.log("Info clicked for id:", id);
    }
  };

  const handleOpenAdjustmentDetail = async (id) => {
    try {
      setAdjustmentDetailLoading(true);
      setAdjustmentDetail(null);
      setIsAdjustmentDetailModalOpen(true);
      const response = await inventoryService.getStockAdjustmentDetail(id);
      setAdjustmentDetail(response?.data || response);
    } catch (err) {
      showNotification(
        "error",
        err.message || "Gagal memuat detail penyesuaian",
      );
      setIsAdjustmentDetailModalOpen(false);
    } finally {
      setAdjustmentDetailLoading(false);
    }
  };

  const handleAdjustmentStatus = async (nextStatus) => {
    if (!adjustmentDetail?.id) return;
    try {
      setAdjustmentActionLoading(true);
      await inventoryService.updateAdjustmentStatus(
        adjustmentDetail.id,
        nextStatus,
      );
      setAdjustmentDetail((prev) => ({ ...prev, status: nextStatus }));
      setAdjustmentData((prev) =>
        prev.map((item) =>
          item.id === adjustmentDetail.id
            ? { ...item, status: nextStatus }
            : item,
        ),
      );
      showNotification(
        "success",
        `Penyesuaian berhasil ${nextStatus === "APPROVED" ? "disetujui" : "ditolak"}`,
      );
      setIsAdjustmentDetailModalOpen(false);
    } catch (err) {
      showNotification(
        "error",
        err.message || "Gagal mengubah status penyesuaian",
      );
    } finally {
      setAdjustmentActionLoading(false);
    }
  };

  // Inline approve/reject langsung dari tabel (tanpa buka modal detail).
  // Pakai endpoint existing updateAdjustmentStatus.
  const handleAdjustmentInlineStatus = async (adjustmentId, nextStatus) => {
    if (!adjustmentId) return;
    const action = nextStatus === "APPROVED" ? "menyetujui" : "menolak";
    if (
      !window.confirm(`Apakah Anda yakin ingin ${action} penyesuaian ini?`)
    ) {
      return;
    }
    try {
      await inventoryService.updateAdjustmentStatus(adjustmentId, nextStatus);
      setAdjustmentData((prev) =>
        prev.map((item) =>
          item.id === adjustmentId ? { ...item, status: nextStatus } : item,
        ),
      );
      showNotification(
        "success",
        `Penyesuaian berhasil ${nextStatus === "APPROVED" ? "disetujui" : "ditolak"}`,
      );
    } catch (err) {
      showNotification(
        "error",
        err.message || "Gagal mengubah status penyesuaian",
      );
    }
  };

  const handleOpenRequestDetail = async (id) => {
    try {
      setRequestDetailLoading(true);
      const response = await inventoryService.getPurchaseOrderDetail(id);
      const detailData = response?.data || response;
      setRequestDetail(detailData);
      setIsRequestDetailModalOpen(true);
    } catch (err) {
      showNotification(
        "error",
        err.message || "Gagal memuat detail permintaan",
      );
    } finally {
      setRequestDetailLoading(false);
    }
  };

  const handleRequestStatus = async (nextStatus, updatedItems = null) => {
    if (!requestDetail?.id) return;
    try {
      setRequestActionLoading(true);

      const payload = { status: nextStatus };
      if (updatedItems && nextStatus === "APPROVED") {
        payload.items = updatedItems;
      }

      await inventoryService.updatePurchaseOrderStatus(
        requestDetail.id,
        payload,
      );

      setRequestDetail((prev) => ({ ...prev, status: nextStatus }));
      setRequestData((prev) =>
        prev.map((item) =>
          item.id === requestDetail.id ? { ...item, status: nextStatus } : item,
        ),
      );

      showNotification(
        "success",
        `Permintaan berhasil ${nextStatus === "APPROVED" ? "disetujui" : "ditolak"}`,
      );
      setIsRequestDetailModalOpen(false);
    } catch (err) {
      showNotification(
        "error",
        err.message || "Gagal mengubah status permintaan",
      );
    } finally {
      setRequestActionLoading(false);
    }
  };

  const handleAdd = () => {
    if (activeTab === "Purchase Order" || activeTab === "Permintaan") {
      setIsAddPOModalOpen(true);
    } else if (activeTab === "Penyesuaian") {
      setIsAdjustmentModalOpen(true);
    } else if (activeTab === "Konversi Stok") {
      setSelectedProduct(null);
      setSelectedProductVariants([]);
      setSelectedProductRules([]);
      setIsAddSellingUnitsModalOpen(true);
    } else if (activeTab === "Label Barcode") {
      setSelectedBarcodeLabel(null);
      setIsCreateBarcodeModalOpen(true);
    }
  };

  const handlePrintBarcodeLabel = (item) => {
    setQuickPrintBarcodeLabel(item);
  };

  const handleDetailBarcodeLabel = (item) => {
    setSelectedBarcodeLabel(item);
    setIsCreateBarcodeModalOpen(true);
  };

  const handleDeleteBarcodeLabel = (item) => {
    setBarcodeLabelToDelete(item);
    setIsBarcodeDeleteModalOpen(true);
  };

  const handleConfirmDeleteBarcodeLabel = async () => {
    if (!barcodeLabelToDelete || barcodeDeleteLoading) return;
    setBarcodeDeleteLoading(true);
    try {
      await barcodeLabelService.delete(barcodeLabelToDelete.id);
      showNotification("success", "Label barcode berhasil dihapus");
      setIsBarcodeDeleteModalOpen(false);
      setBarcodeLabelToDelete(null);
      await fetchData();
    } catch (err) {
      showNotification("error", err.message || "Gagal menghapus label barcode");
    } finally {
      setBarcodeDeleteLoading(false);
    }
  };

  const handleEditRule = async (rule) => {
    try {
      setLoadingEditId(rule.id);
      const prodRes = await masterService.products.get(rule.product_id);
      const varRes = await masterService.products.variants.list(rule.product_id);
      const rulesRes = await conversionService.rules.list({ product_id: rule.product_id });

      setSelectedProduct(prodRes.data || prodRes);
      setSelectedProductVariants(varRes.data || []);
      const rulesData = rulesRes.data || rulesRes;
      setSelectedProductRules(
        Array.isArray(rulesData) ? rulesData : (rulesData.items || rulesData.data || [])
      );
      setEditingUnitId(rule.from_unit_id);
      setIsAddSellingUnitsModalOpen(true);
    } catch (err) {
      showNotification("error", "Gagal memuat data produk untuk diedit");
    } finally {
      setLoadingEditId(null);
    }
  };

  const handleInfoRule = (rule) => {
    setDetailData(rule);
    setIsDetailModalOpen(true);
  };

  const handleDeleteRuleClick = (ids) => {
    // ids can be a single id or an array (from grouped table rows)
    setDeleteIds(Array.isArray(ids) ? ids : [ids]);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteRuleConfirm = async () => {
    if (!deleteIds || deleteIds.length === 0) return;
    try {
      setLoading(true);
      await Promise.all(deleteIds.map((id) => conversionService.rules.delete(id)));
      showNotification("success", "Aturan konversi berhasil dihapus");
      setRefreshTrigger((prev) => prev + 1);
    } catch (err) {
      showNotification("error", err.message || "Gagal menghapus aturan konversi");
    } finally {
      setLoading(false);
      setIsDeleteModalOpen(false);
      setDeleteIds([]);
    }
  };

  const getSelectedProductName = () => {
    const selected = products.find((p) => p.id === filterProduct);
    return selected ? selected.name : "";
  };

  const handleToggleRuleStatus = async (id, currentStatus) => {
    try {
      setLoading(true);
      await conversionService.rules.update(id, { is_active: !currentStatus });
      showNotification(
        "success",
        `Aturan konversi berhasil ${!currentStatus ? "diaktifkan" : "dinonaktifkan"}`,
      );
      setRefreshTrigger((prev) => prev + 1);
    } catch (err) {
      showNotification("error", err.message || "Gagal mengubah status aturan");
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePO = async (data) => {
    try {
      setLoading(true);
      await inventoryService.createPurchaseOrder(data);
      showNotification("success", "Purchase Order berhasil dibuat");
      setIsAddPOModalOpen(false);
      setCurrentPage(1);
      setRefreshTrigger((prev) => prev + 1);
      await fetchData();
    } catch (err) {
      console.error("Failed to create PO:", err);
      showNotification("error", err.message || "Gagal membuat Purchase Order");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const getStatusLabel = (status) => {
    const map = {
      DRAFT: "Draft",
      SUBMITTED: "Diajukan",
      APPROVED: "Disetujui",
      ORDERED: "Dipesan",
      PARTIAL_RECEIVED: "Diterima Sebagian",
      COMPLETED: "Selesai",
      CANCELLED: "Dibatalkan",
      REJECTED: "Ditolak",
    };
    return map[status] || status || "-";
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(Number(amount || 0));
  };

  const handleOpenProcurementDetail = async (id) => {
    try {
      setProcurementDetailLoading(true);
      const response = await inventoryService.getPurchaseOrderDetail(id);
      const detailData = response?.data || response;
      setProcurementDetail(detailData);
      setIsProcurementDetailModalOpen(true);
    } catch (err) {
      showNotification("error", err.message || "Gagal memuat detail pengadaan");
    } finally {
      setProcurementDetailLoading(false);
    }
  };

  const handlePrintProcurementReceipt = async (id) => {
    try {
      setLoading(true);
      const response = await inventoryService.getPurchaseOrderDetail(id);
      const detailData = response?.data || response;
      setPrintData(detailData);
    } catch (err) {
      showNotification(
        "error",
        err.message || "Gagal memuat data untuk dicetak",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleProcurementStatus = async (nextStatus) => {
    if (!procurementDetail?.id) return;
    try {
      setProcurementActionLoading(true);
      await inventoryService.updatePurchaseOrderStatus(procurementDetail.id, {
        status: nextStatus,
      });
      showNotification(
        "success",
        `Status pengadaan diubah ke ${getStatusLabel(nextStatus)}`,
      );
      setIsProcurementDetailModalOpen(false);
      setProcurementDetail(null);
      await fetchData();
    } catch (err) {
      showNotification(
        "error",
        err.message || "Gagal mengubah status pengadaan",
      );
    } finally {
      setProcurementActionLoading(false);
    }
  };

  const handleUpdatePOStatus = async (id, nextStatus) => {
    try {
      setLoading(true);
      await inventoryService.updatePurchaseOrderStatus(id, {
        status: nextStatus,
      });
      showNotification(
        "success",
        `Status Purchase Order berhasil diubah ke ${getStatusLabel(nextStatus)}`,
      );
      await fetchData();
    } catch (err) {
      showNotification(
        "error",
        err.message || "Gagal mengubah status Purchase Order",
      );
    } finally {
      setLoading(false);
    }
  };

  // Calculate pagination values
  const startEntry = meta.total === 0 ? 0 : (meta.page - 1) * meta.limit + 1;
  const endEntry = Math.min(meta.page * meta.limit, meta.total);
  const maxPages = Math.ceil(meta.total / meta.limit);

  return (
    <div className="flex h-screen bg-slate-50">
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggle={() =>
          setIsSidebarCollapsed((prev) => {
            const newState = !prev;
            localStorage.setItem("sidebarCollapsed", JSON.stringify(newState));
            return newState;
          })
        }
        activePage="inventori"
      />

      <div
        className={`flex-1 overflow-auto transition-all duration-300 ${isSidebarCollapsed ? "ml-16" : "ml-48"}`}>
        <Header />

        <div className="p-6">
          {/* Top Card: Page Title & Actions */}
          <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-sm p-4 mb-5">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              {/* Left: Page Title */}
              <div className="flex items-center gap-3 w-full md:w-auto">
                <div className="w-9 h-9 bg-teal-50 rounded-lg flex items-center justify-center shrink-0">
                  <Boxes className="w-4 h-4 text-teal-600" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-base font-bold text-gray-900">
                    Manajemen Inventori
                  </h2>
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    Kelola stok dan konversi produk
                  </p>
                </div>
              </div>

              {/* Right: Actions */}
              <div className="flex items-center gap-2.5 w-full md:w-auto">


                <div className="relative flex-1 md:w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Cari data . . ."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 focus:bg-white transition-colors"
                  />
                </div>

                {/* Filter button — tersedia di tab Persediaan, Konversi Stok, dan Purchase Order */}
                {(activeTab === "Persediaan" || activeTab === "Konversi Stok" || activeTab === "Purchase Order") && (
                  <div className="relative" ref={filterMenuRef}>
                    <button
                      type="button"
                      onClick={() => setIsFilterOpen((prev) => !prev)}
                      className={`p-2 ring-1 rounded-lg transition-colors relative ${(activeTab === "Purchase Order" ? (poStatusFilter && poStatusFilter !== "ALL") : categoryFilter)
                        ? "bg-teal-50 ring-teal-300 text-teal-700"
                        : "bg-white ring-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                        }`}
                      title="Filter">
                      <Filter className="w-4 h-4" />
                      {(activeTab === "Purchase Order" ? (poStatusFilter && poStatusFilter !== "ALL") : categoryFilter) && (
                        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-teal-500 rounded-full ring-2 ring-white" />
                      )}
                    </button>

                    {isFilterOpen && (
                      <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-lg ring-1 ring-slate-200/70 z-30 overflow-visible">
                        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between rounded-t-xl bg-white">
                          <p className="text-sm font-semibold text-slate-800">
                            {activeTab === "Purchase Order" ? "Filter Status PO" : "Filter Kategori"}
                          </p>
                          <button
                            onClick={() => setIsFilterOpen(false)}
                            className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                            aria-label="Tutup filter">
                            <X className="w-4 h-4" />
                          </button>
                        </div>

                        {activeTab === "Purchase Order" ? (
                          <div className="p-4 space-y-3">
                            <div>
                              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
                                <Tag className="w-3 h-3" /> Status PO
                              </p>
                              <SearchableSelect
                                options={[
                                  { value: "ALL", label: "Semua Status" },
                                  { value: "DRAFT", label: "Draft" },
                                  { value: "SUBMITTED", label: "Menunggu Persetujuan" },
                                  { value: "APPROVED", label: "Disetujui" },
                                  { value: "ORDERED", label: "Sedang Dipesan" },
                                  { value: "PARTIAL_RECEIVED", label: "Diterima Sebagian" },
                                  { value: "COMPLETED", label: "Selesai Diterima" },
                                  { value: "CANCELLED", label: "Dibatalkan" },
                                  { value: "REJECTED", label: "Ditolak" },
                                ]}
                                value={poStatusFilter || "ALL"}
                                onChange={(val) => {
                                  setPoStatusFilter(val || "ALL");
                                  setCurrentPage(1);
                                }}
                                placeholder="Semua Status"
                                displayKey="label"
                                valueKey="value"
                                searchable={false}
                                size="sm"
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="p-4 space-y-3">
                            <div>
                              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
                                <Tag className="w-3 h-3" /> Filter Kategori
                              </p>
                              <SearchableSelect
                                options={[{ id: "", name: "Semua Kategori" }, ...categories]}
                                value={categoryFilter}
                                onChange={(value) => {
                                  setCategoryFilter(value);
                                  setCurrentPage(1);
                                }}
                                placeholder="Semua Kategori"
                                displayKey="name"
                                valueKey="id"
                                minSearchLength={1}
                                size="sm"
                              />
                            </div>
                          </div>
                        )}

                        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center justify-between rounded-b-xl">
                          <button
                            onClick={() => {
                              if (activeTab === "Purchase Order") {
                                setPoStatusFilter("ALL");
                              } else {
                                setCategoryFilter("");
                              }
                              setCurrentPage(1);
                            }}
                            disabled={activeTab === "Purchase Order" ? (!poStatusFilter || poStatusFilter === "ALL") : !categoryFilter}
                            className="text-xs font-semibold text-rose-600 hover:text-rose-700 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors">
                            Reset
                          </button>
                          <button
                            onClick={() => setIsFilterOpen(false)}
                            className="px-3 py-1.5 rounded-lg bg-teal-600 text-white text-xs font-semibold hover:bg-teal-700 transition-colors">
                            Terapkan
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Export Excel & Konfigurasi Stok button — hanya tersedia di tab Persediaan */}
                {activeTab === "Persediaan" && (
                  <>
                    <button
                      type="button"
                      onClick={handleExportStockExcel}
                      disabled={isExportingStock || (allStockGroups.length === 0 && stockData.length === 0)}
                      className="p-2 bg-emerald-50 hover:bg-emerald-100 ring-1 ring-emerald-300 rounded-lg text-emerald-800 transition-colors flex items-center justify-center shrink-0 disabled:opacity-50 cursor-pointer shadow-xs"
                      title="Export Excel Persediaan Stok (.xlsx)">
                      {isExportingStock ? (
                        <div className="w-4 h-4 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <ExcelIcon className="w-4 h-4" />
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => setIsConfigModalOpen(true)}
                      className="p-2 bg-amber-50 ring-1 ring-amber-200 rounded-lg hover:bg-amber-100 text-amber-600 transition-colors"
                      title="Konfigurasi Stok">
                      <Settings className="w-4 h-4" />
                    </button>
                  </>
                )}

                {(activeTab === "Purchase Order" ||
                  activeTab === "Permintaan" ||
                  activeTab === "Penyesuaian" ||
                  activeTab === "Konversi Stok" ||
                  activeTab === "Label Barcode") && (
                    <button
                      onClick={handleAdd}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors whitespace-nowrap bg-teal-600 text-white hover:bg-teal-700 shadow-sm shadow-teal-600/20"
                    >
                      <Plus className="w-4 h-4" />
                      <span>{activeTab === "Label Barcode" ? "Buat Label Barcode" : activeTab === "Purchase Order" ? "Buat PO" : "Tambah"}</span>
                    </button>
                  )}
              </div>
            </div>
          </div>

          {/* Tabs Navigation */}
          <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 p-3 mb-6 shadow-sm">
            <div className="overflow-x-auto -mx-1 px-1">
              <div className="flex items-center min-w-max gap-2">
                {tabs.map((tab) => {
                  const Icon = tab.icon || Boxes;
                  const isActive = activeTab === tab.label;
                  return (
                    <button
                      key={tab.label}
                      onClick={() => {
                        const path =
                          tab.label === "Persediaan"
                            ? "/inventori/persediaan"
                            : tab.label === "Penyesuaian"
                              ? "/inventori/penyesuaian"
                              : tab.label === "Purchase Order"
                                ? "/inventori/purchase-orders"
                                : tab.label === "Permintaan"
                                  ? "/inventori/permintaan"
                                  : tab.label === "Pengadaan"
                                    ? "/inventori/pengadaan"
                                    : tab.label === "Penerimaan"
                                      ? "/inventori/penerimaan"
                                      : tab.label === "Stok Ekstra"
                                        ? "/inventori/stok-ekstra"
                                        : tab.label === "Label Barcode"
                                          ? "/inventori/label-barcode"
                                          : "/inventori/konversi";
                        navigate(path);
                      }}
                      className={`group relative inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${isActive
                        ? "bg-gradient-to-br from-teal-600 to-emerald-600 text-white shadow-md shadow-teal-600/30 ring-1 ring-teal-500"
                        : "bg-slate-50 text-slate-600 ring-1 ring-transparent hover:text-teal-700 hover:bg-teal-50 hover:ring-teal-200"
                        }`}>
                      <span
                        className={`w-6 h-6 rounded-lg flex items-center justify-center transition-colors ${isActive
                          ? "bg-white/20"
                          : "bg-white text-teal-600 group-hover:bg-teal-100"
                          }`}>
                        <Icon className="w-3.5 h-3.5" />
                      </span>
                      {tab.label}
                      {tab.count !== undefined && (
                        <span
                          className={`text-xs font-bold ${isActive ? "text-amber-200" : "text-slate-400"
                            }`}>
                          ({tab.count})
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Table Content */}
          {activeTab === "Persediaan" && (
            <StockTable
              data={stockData}
              loading={loading}
              error={error}
              conversionRules={stockConversionRules}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              totalEntries={meta.total}
              startEntry={startEntry}
              endEntry={endEntry}
              maxPages={maxPages}
              onInfo={handleInfo}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          )}

          {activeTab === "Penyesuaian" && (
            <AdjustmentTable
              data={adjustmentData}
              loading={loading}
              error={error}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              totalEntries={meta.total}
              startEntry={startEntry}
              endEntry={endEntry}
              maxPages={maxPages}
              onInfo={handleInfo}
              onApprove={(id) =>
                handleAdjustmentInlineStatus(id, "APPROVED")
              }
              onReject={(id) =>
                handleAdjustmentInlineStatus(id, "REJECTED")
              }
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
              onSort={handleSort}
              sortBy={sortBy}
              sortOrder={sortOrder}
            />
          )}

          {activeTab === "Purchase Order" && (
            <PurchaseOrderTable
              data={purchaseOrderData}
              loading={loading}
              error={error}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              totalEntries={meta.total}
              startEntry={startEntry}
              endEntry={endEntry}
              maxPages={maxPages}
              selectedStatus={poStatusFilter}
              onStatusFilterChange={(status) => {
                setPoStatusFilter(status);
                setCurrentPage(1);
              }}
              onCreatePO={() => setIsAddPOModalOpen(true)}
              onDetail={(id) => navigate(`/inventori/purchase-orders/${id}`)}
              onPrint={(id) => navigate(`/inventori/purchase-orders/${id}/print`)}
              onQuickReceive={(id) => navigate(`/inventori/purchase-orders/${id}`, { state: { autoReceive: true } })}
              onUpdateStatus={handleUpdatePOStatus}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
              onSort={handleSort}
              sortBy={sortBy}
              sortOrder={sortOrder}
            />
          )}

          {activeTab === "Permintaan" && (
            <RequestTable
              data={requestData}
              loading={loading}
              error={error}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              totalEntries={meta.total}
              startEntry={startEntry}
              endEntry={endEntry}
              maxPages={maxPages}
              onInfo={handleInfo}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
              onSort={handleSort}
              sortBy={sortBy}
              sortOrder={sortOrder}
            />
          )}

          {activeTab === "Pengadaan" && (
            <ProcurementTable
              data={procurementData}
              loading={loading}
              error={error}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              totalEntries={meta.total}
              startEntry={startEntry}
              endEntry={endEntry}
              maxPages={maxPages}
              onPrint={handlePrintProcurementReceipt}
              onDetail={handleOpenProcurementDetail}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
              onSort={handleSort}
              sortBy={sortBy}
              sortOrder={sortOrder}
            />
          )}

          {activeTab === "Penerimaan" && (
            <ReceivingTable
              data={receivingData}
              loading={loading}
              error={error}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              totalEntries={meta.total}
              startEntry={startEntry}
              endEntry={endEntry}
              maxPages={maxPages}
              onInfo={handleInfo}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
              onSort={handleSort}
              sortBy={sortBy}
              sortOrder={sortOrder}
            />
          )}

          {activeTab === "Konversi Stok" && (
            <ConversionRulesTable
              data={conversionRules}
              loading={loading}
              error={error}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              totalEntries={meta.total}
              startEntry={startEntry}
              endEntry={endEntry}
              maxPages={maxPages}
              onDelete={handleDeleteRuleClick}
              onEdit={handleEditRule}
              onInfo={handleInfoRule}
              onToggleStatus={handleToggleRuleStatus}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
              loadingEditId={loadingEditId}
            />
          )}

          {/* ── Stok Ekstra ─────────────────────────────────── */}
          {activeTab === "Stok Ekstra" && (
            <AddonStockTable searchTerm={searchTerm} />
          )}

          {/* ── Label Barcode ─────────────────────────────────── */}
          {activeTab === "Label Barcode" && (
            <DataBarcodeLabelTable
              data={barcodeLabelData}
              loading={loading}
              onPrint={handlePrintBarcodeLabel}
              onDetail={handleDetailBarcodeLabel}
              onDelete={handleDeleteBarcodeLabel}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              totalEntries={meta.total}
              startEntry={startEntry}
              endEntry={endEntry}
              maxPages={maxPages}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          )}
        </div>
      </div>

      <AddProcurementModal
        isOpen={isAddPOModalOpen}
        onClose={() => setIsAddPOModalOpen(false)}
        onSubmit={handleCreatePO}
      />

      <StockConfigModal
        isOpen={isConfigModalOpen}
        onClose={() => {
          setIsConfigModalOpen(false);
          setRefreshTrigger((prev) => prev + 1);
        }}
        onSave={() => {
          setRefreshTrigger((prev) => prev + 1);
          fetchData();
        }}
      />

      <StockAdjustmentModal
        isOpen={isAdjustmentModalOpen}
        onClose={() => setIsAdjustmentModalOpen(false)}
        onSave={() => {
          setIsAdjustmentModalOpen(false);
          setRefreshTrigger((prev) => prev + 1);
        }}
      />

      <ProcurementDetailModal
        isOpen={isProcurementDetailModalOpen}
        onClose={() => setIsProcurementDetailModalOpen(false)}
        data={procurementDetail}
        loading={procurementActionLoading}
        onUpdateStatus={handleProcurementStatus}
      />

      <RequestDetailModal
        isOpen={isRequestDetailModalOpen}
        onClose={() => setIsRequestDetailModalOpen(false)}
        data={requestDetail}
        loading={requestActionLoading}
        onUpdateStatus={handleRequestStatus}
      />

      <AdjustmentDetailModal
        isOpen={isAdjustmentDetailModalOpen}
        onClose={() => setIsAdjustmentDetailModalOpen(false)}
        data={adjustmentDetail}
        loading={adjustmentActionLoading}
        onUpdateStatus={handleAdjustmentStatus}
      />

      <AddSellingUnitsModal
        isOpen={isAddSellingUnitsModalOpen}
        onClose={() => {
          setIsAddSellingUnitsModalOpen(false);
          setSelectedProduct(null);
          setSelectedProductVariants([]);
          setSelectedProductRules([]);
          setEditingUnitId(null);
        }}
        productId={selectedProduct?.id}
        product={selectedProduct}
        existingVariants={selectedProductVariants}
        existingConversionRules={selectedProductRules}
        editingUnitId={editingUnitId}
        onSuccess={() => setRefreshTrigger((prev) => prev + 1)}
      />

      <CreateBarcodeLabelModal
        isOpen={isCreateBarcodeModalOpen}
        onClose={() => {
          setIsCreateBarcodeModalOpen(false);
          setSelectedBarcodeLabel(null);
        }}
        existingData={selectedBarcodeLabel}
        onSuccess={() => {
          setIsCreateBarcodeModalOpen(false);
          setSelectedBarcodeLabel(null);
          fetchData();
        }}
      />

      <BarcodeLabelQuickPrint
        record={quickPrintBarcodeLabel}
        onDone={() => setQuickPrintBarcodeLabel(null)}
      />

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteRuleConfirm}
        title="Konfirmasi Hapus"
        message="Apakah Anda yakin ingin menghapus aturan konversi ini? Data yang dihapus tidak dapat dikembalikan."
        type="danger"
        confirmText="Hapus"
      />

      <ConfirmationModal
        isOpen={isBarcodeDeleteModalOpen}
        onClose={() => {
          if (barcodeDeleteLoading) return;
          setIsBarcodeDeleteModalOpen(false);
          setBarcodeLabelToDelete(null);
        }}
        onConfirm={handleConfirmDeleteBarcodeLabel}
        title="Hapus Label Barcode?"
        message={`Apakah Anda yakin ingin menghapus label barcode "${barcodeLabelToDelete?.name || "ini"}"? Data yang dihapus tidak dapat dikembalikan.`}
        type="danger"
        confirmText="Hapus"
        isLoading={barcodeDeleteLoading}
      />

      {/* Detail Conversion Modal */}
      {isDetailModalOpen && detailData && (
        <Modal
          isOpen={isDetailModalOpen}
          onClose={() => setIsDetailModalOpen(false)}
          title="Detail Aturan Konversi"
        >
          <div className="space-y-4">
            <div className="bg-slate-50 ring-1 ring-slate-200 rounded-xl p-4 space-y-3">
              <div>
                <p className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Nama Produk</p>
                <p className="text-sm font-semibold text-slate-800">{detailData.product_name || "-"}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Satuan Awal</p>
                  <p className="text-sm font-semibold text-slate-800">{detailData.from_unit_name || "-"}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Satuan Tujuan</p>
                  <p className="text-sm font-semibold text-slate-800">{detailData.to_unit_name || "-"}</p>
                </div>
              </div>
              <div>
                <p className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Nilai Konversi</p>
                <p className="text-sm font-semibold text-slate-800">
                  1 {detailData.from_unit_name} = {detailData.conversion_factor} {detailData.to_unit_name}
                </p>
              </div>
              {detailData.variantPrices && detailData.variantPrices.length > 0 ? (
                <div>
                  <p className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Harga Jual per Varian</p>
                  <div className="mt-2 space-y-2">
                    {detailData.variantPrices.map((vp) => (
                      <div key={vp.variant_id} className="flex justify-between items-center py-1 border-b border-slate-100 last:border-0">
                        <span className="text-sm font-medium text-slate-700">{vp.variant_name}</span>
                        <span className="text-sm font-bold text-teal-600">{formatCurrency(vp.sell_price)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Harga Jual Konversi</p>
                  <p className="text-sm font-bold text-teal-600">
                    {formatCurrency(detailData.sell_price)}
                  </p>
                </div>
              )}
              <div>
                <p className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Stok Tersedia</p>
                <p className="text-sm font-medium text-slate-700">
                  {detailData.total_stock || detailData.current_stock} {detailData.to_unit_name} ({detailData.conversion_factor ? Math.floor((detailData.total_stock || detailData.current_stock) / detailData.conversion_factor) : 0} {detailData.from_unit_name})
                </p>
              </div>
            </div>
            <div className="flex justify-end pt-2">
              <button
                onClick={() => setIsDetailModalOpen(false)}
                className="px-5 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors font-medium text-sm"
              >
                Tutup
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Hidden Print Component */}
      <div style={{ display: "none" }}>
        <div ref={printComponentRef}>
          <ProcurementInvoice data={printData} />
        </div>
      </div>
    </div>
  );
}
